/**
 * Résolution du subdomaine tenant côté front, AVANT authentification.
 *
 * WHY (multi-tenant CLAUDE.md §4) : sur les pages publiques (login,
 * landing) il n'y a pas de JWT donc on ne peut pas appeler `/api/v1/settings`.
 * On utilise donc le hostname (`<tenant>.skola.app`) pour deviner le tenant
 * et appeler l'endpoint public `/public/c/:subdomain/catalog` qui inclut
 * les champs de branding (name, logo, tagline).
 *
 * Stratégie de résolution :
 *   - `efcl.skola.app` → "efcl"
 *   - `localhost` / `127.0.0.1` → `null` (dev local, fallback Scola)
 *   - `skola.app` / `www.skola.app` → `null` (landing marketing, fallback Scola)
 *   - URL avec `?tenant=efcl` (override pour dev/QA) → "efcl"
 *
 * Fail-safe : toute ambiguïté retourne `null` — l'UI tombe sur le branding
 * Scola générique. Pas d'erreur visible (charte §6 fail-fast côté code,
 * fail-safe côté UX pour branding non-critique).
 */
export function currentTenantSubdomain(): string | null {
  if (typeof window === "undefined") return null;

  // Override explicite via ?tenant=efcl — pratique en dev / QA / preview Vercel.
  // Validation : 1-63 chars, [a-z0-9-], pas d'underscore ni de point.
  try {
    const qsTenant = new URLSearchParams(window.location.search).get("tenant");
    if (qsTenant && /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(qsTenant)) {
      return qsTenant;
    }
  } catch {
    // window.location.search inaccessible — on continue avec le hostname.
  }

  const host = window.location.hostname.toLowerCase();
  if (!host || host === "localhost" || host === "127.0.0.1" || host === "::1") {
    return null;
  }

  // Domaines produit (pas un tenant) : scola.vercel.app, leman.vercel.app, …
  const PLATFORM_ROOTS = new Set([
    "scola.vercel.app",
    "scola-app.vercel.app",
    "scola-ch.vercel.app",
    "leman.vercel.app",
    "www.scola.vercel.app",
  ]);
  if (PLATFORM_ROOTS.has(host)) return null;

  // IP littérale (IPv4) — pas de subdomaine extractible.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;

  const labels = host.split(".");
  // Hostname sans subdomaine : `skola.app`, `example.ch` → 2 labels → fallback.
  // `www.skola.app` → 3 labels mais on retire `www`.
  if (labels.length < 3) return null;

  let first = labels[0];
  if (!first || first === "www") return null;

  // Vercel preview URLs : `<project>.vercel.app` avec project nommé
  // `skola-<tenant>` par convention. On retire le préfixe `skola-` pour
  // retrouver le subdomain canonique tel qu'enregistré en DB.
  //
  // WHY (multi-tenant CLAUDE.md §4) : le subdomain canonique reste le nom
  // tenant (ex. "bimleman"), pas l'URL Vercel par défaut. Quand un custom
  // domain `bimleman.skola.app` sera configuré, le résolveur tombe sur le
  // même subdomain DB sans migration.
  if (labels[labels.length - 1] === "app" && labels[labels.length - 2] === "vercel") {
    first = first.replace(/^skola-/, "");
  }

  // Validation finale : un label DNS valide (RFC 1035).
  if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(first)) return null;

  return first;
}
